import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Plus,
  Users,
  BookOpen,
  Clock,
  X,
  Search,
  UserPlus,
  Trash2,
} from 'lucide-react'
import * as api from '../../app/axios/api.jsx'
import { toast } from 'sonner'

const BatchDetails = () => {
  const { id } = useParams()
  const Navigate = useNavigate()
  const [batchData] = useState({
    id: 1,
    name: '00XX',
    code: 'Inf-001',
    startDate: '0000-00-00',
    students: 0,
    instructor: 'XX00',
    description:
      'Demo batch for testing purposes. This batch is designed to provide an overview of the course structure and content. Students will get hands-on experience with practical examples and real-world applications.',
  })

  const [students, setStudents] = useState([])
  const [lectures, setLectures] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showAddStudentModal, setShowAddStudentModal] = useState(false)
  const [showCreateLectureModal, setShowCreateLectureModal] = useState(false)
  const [searchStudent, setSearchStudent] = useState('')
  const [activeTab, setActiveTab] = useState('students')
  const [studentsData, setStudentsData] = useState([])
  const [selectedStudents, setSelectedStudents] = useState([])

  // Modal Status Filter State (Default: 'Running')
  const [modalStatusFilter, setModalStatusFilter] = useState('Running')

  const [newLecture, setNewLecture] = useState({
    teacher: '',
    note: '',
    lecture_date: '',
    lecture_start_time: '',
    lecture_end_time: '',
    seminar_hall: '',
    topic: '',
  })

  // *************************** Add Student to Batch *********************

  const handleAddMultipleStudents = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student to add.')
      return
    }

    setIsLoading(true)
    try {
      const payload = {
        admission_ids: selectedStudents.map((id) => parseInt(id, 10)),
        batch_id: parseInt(id, 10),
      }

      await api.AddStdBatch(payload)

      toast.success(
        `${selectedStudents.length} student(s) added to batch successfully!`,
      )
      handleCloseAddStudentModal()
      fetchBatchStudent(id)
    } catch (error) {
      console.error('Error adding students to batch:', error)
      toast.error(
        error?.response?.data?.message || 'Failed to add students to batch.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteBatchStd = async (batchStdId) => {
    setIsLoading(true)
    try {
      await api.deleteStdBatch(batchStdId)
      toast.success('Batch Student deleted successfully!')
      fetchBatchStudent(id)
    } catch (error) {
      console.error('Error deleting lecture:', error)
      toast.error(error?.response?.data?.message || 'Failed to delete lecture.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchBatchStudent = async (id) => {
    setIsLoading(true)
    try {
      const res = await api.fetchStdBatch(id)
      setStudents(res.data)
    } catch (error) {
      console.error(error)
      toast.error(error?.response?.data?.message || 'Failed to fetch lectures.')
    } finally {
      setIsLoading(false)
    }
  }

  // *************************** Create Lecture *********************
  const handleCreateLecture = async (e) => {
    e.preventDefault()

    if (
      !newLecture.teacher ||
      !newLecture.topic ||
      !newLecture.lecture_date ||
      !newLecture.lecture_start_time ||
      !newLecture.lecture_end_time ||
      !newLecture.seminar_hall
    ) {
      toast.error('Please fill in all required fields')
      return
    }

    setIsLoading(true)

    try {
      const formatDateTime = (date, time) => {
        const dateTimeString = `${date}T${time}:00+05:30`
        return new Date(dateTimeString).toISOString().slice(0, -1) + '+05:30'
      }
      const batchIdAsNumber = parseInt(id, 10)

      const lectureData = {
        teacher: newLecture.teacher,
        note: newLecture.note,
        lecture_start_time: formatDateTime(
          newLecture.lecture_date,
          newLecture.lecture_start_time,
        ),
        lecture_end_time: formatDateTime(
          newLecture.lecture_date,
          newLecture.lecture_end_time,
        ),
        seminar_hall: newLecture.seminar_hall,
        topic: newLecture.topic,
        batch_id: batchIdAsNumber,
      }

      await api.createLecBatch(lectureData)

      toast.success('Lecture created successfully!')
      setNewLecture({
        teacher: '',
        note: '',
        lecture_date: '',
        lecture_start_time: '',
        lecture_end_time: '',
        seminar_hall: '',
        topic: '',
      })
      setShowCreateLectureModal(false)
      fetchLectures(id)
    } catch (error) {
      console.error('Error creating lecture:', error)
      toast.error(error?.response?.data?.message || 'Failed to create lecture.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteLecture = async (lectureId) => {
    setIsLoading(true)
    try {
      await api.deleteLceBatch(lectureId)
      toast.success('Lecture deleted successfully!')
      fetchLectures(id)
    } catch (error) {
      console.error('Error deleting lecture:', error)
      toast.error(error?.response?.data?.message || 'Failed to delete lecture.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchLectures = async (id) => {
    setIsLoading(true)
    try {
      const res = await api.fetchLecBatch(id)
      setLectures(res.data)
    } catch (error) {
      console.error('Error fetching lectures:', error)
      toast.error(error?.response?.data?.message || 'Failed to fetch lectures.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAllStudent = async () => {
    setIsLoading(true)
    try {
      const res = await api.allStudent()
      setStudentsData(res.data)
    } catch (error) {
      console.error('Error fetching lectures:', error)
      toast.error(error?.response?.data?.message || 'Failed to fetch lectures.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    Navigate('/batch')
  }

  const handleCloseAddStudentModal = () => {
    setShowAddStudentModal(false)
    setModalStatusFilter('Running') // Reset filter to default
    setSelectedStudents([])
    setSearchStudent('')
  }

  // Filter batch students list based on search input
  const filteredStudents = students.filter((student) => {
    const studentName = student.name?.toLowerCase() || ''
    const studentEmail = student.email?.toLowerCase() || ''
    const searchTerm = searchStudent.toLowerCase()

    return studentName.includes(searchTerm) || studentEmail.includes(searchTerm)
  })

  const activeStudents = students.filter(
    (student) => student.status === 'Active',
  ).length

  // Helper utility functions for modal selection
  const currentBatchIdNum = parseInt(id, 10)

  const isAlreadyAdded = (student) => {
    return (
      students.some(
        (s) => s.id === student.id || s.admission_id === student.id,
      ) || student.batch_id === currentBatchIdNum
    )
  }

  // Filter studentsData based on selected Status Filter and Search Input
  const filteredModalStudents = studentsData
    .filter((student) => {
      const statusMatch =
        student.status?.toLowerCase() === modalStatusFilter.toLowerCase()
      return statusMatch
    })
    .filter((student) => {
      const name = student.name?.toLowerCase() || ''
      const email = student.email?.toLowerCase() || ''
      const course = student.course?.toLowerCase() || ''
      const searchTerm = searchStudent.toLowerCase()

      return (
        name.includes(searchTerm) ||
        email.includes(searchTerm) ||
        course.includes(searchTerm)
      )
    })

  useEffect(() => {
    fetchLectures(id)
    fetchAllStudent()
    fetchBatchStudent(id)
  }, [id])

  return (
    <div className='min-h-screen bg-gray-50'>
      {/* Header */}
      <header className='bg-white shadow-sm border-b sticky top-0 z-20'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex items-center justify-between h-16'>
            <div className='flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1'>
              <button
                onClick={handleBack}
                className='p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0'
              >
                <ArrowLeft className='w-5 h-5 text-gray-600' />
              </button>
              <div className='min-w-0 flex-1'>
                <h1 className='text-lg sm:text-2xl font-bold text-gray-900 truncate'>
                  {batchData.name}
                </h1>
                <p className='text-xs sm:text-sm text-gray-500 truncate'>
                  Batch Code: {batchData.code}
                </p>
              </div>
            </div>
            <div className='flex items-center space-x-2 sm:space-x-3 flex-shrink-0'>
              <button
                onClick={() => setShowAddStudentModal(true)}
                className='inline-flex items-center px-2 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors'
              >
                <UserPlus className='w-4 h-4 sm:mr-2' />
                <span className='hidden sm:inline'>Add Student</span>
              </button>
              <button
                onClick={() => setShowCreateLectureModal(true)}
                className='inline-flex items-center px-2 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors'
              >
                <Plus className='w-4 h-4 sm:mr-2' />
                <span className='hidden sm:inline'>Create Lecture</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8'>
        {/* Stats Cards */}
        <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8'>
          <div className='bg-white rounded-xl shadow-sm border p-3 sm:p-6'>
            <div className='flex items-center'>
              <div className='p-2 bg-blue-100 rounded-lg flex-shrink-0'>
                <Users className='w-4 h-4 sm:w-6 sm:h-6 text-blue-600' />
              </div>
              <div className='ml-2 sm:ml-4 min-w-0'>
                <p className='text-xs sm:text-sm font-medium text-gray-600'>
                  Total Students
                </p>
                <p className='text-lg sm:text-2xl font-bold text-gray-900'>
                  {students.length}
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-xl shadow-sm border p-3 sm:p-6'>
            <div className='flex items-center'>
              <div className='p-2 bg-green-100 rounded-lg flex-shrink-0'>
                <Users className='w-4 h-4 sm:w-6 sm:h-6 text-green-600' />
              </div>
              <div className='ml-2 sm:ml-4 min-w-0'>
                <p className='text-xs sm:text-sm font-medium text-gray-600'>
                  Active Students
                </p>
                <p className='text-lg sm:text-2xl font-bold text-gray-900'>
                  {activeStudents}
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-xl shadow-sm border p-3 sm:p-6'>
            <div className='flex items-center'>
              <div className='p-2 bg-purple-100 rounded-lg flex-shrink-0'>
                <BookOpen className='w-4 h-4 sm:w-6 sm:h-6 text-purple-600' />
              </div>
              <div className='ml-2 sm:ml-4 min-w-0'>
                <p className='text-xs sm:text-sm font-medium text-gray-600'>
                  Total Lectures
                </p>
                <p className='text-lg sm:text-2xl font-bold text-gray-900'>
                  {lectures.length}
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-xl shadow-sm border p-3 sm:p-6'>
            <div className='flex items-center'>
              <div className='p-2 bg-orange-100 rounded-lg flex-shrink-0'>
                <Clock className='w-4 h-4 sm:w-6 sm:h-6 text-orange-600' />
              </div>
              <div className='ml-2 sm:ml-4 min-w-0'>
                <p className='text-xs sm:text-sm font-medium text-gray-600'>
                  Total Lectures
                </p>
                <p className='text-lg sm:text-2xl font-bold text-gray-900'>
                  {lectures.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Batch Info Card */}
        <div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6 mb-6 sm:mb-8'>
          <h3 className='text-base sm:text-lg font-semibold text-gray-900 mb-4'>
            Batch Information
          </h3>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'>
            <div>
              <p className='text-xs sm:text-sm font-medium text-gray-600'>
                Start Date
              </p>
              <p className='text-sm sm:text-lg text-gray-900'>
                {new Date(batchData.startDate).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className='text-xs sm:text-sm font-medium text-gray-600'>
                Instructor
              </p>
              <p className='text-sm sm:text-lg text-gray-900'>
                {batchData.instructor}
              </p>
            </div>
            <div className='sm:col-span-2 lg:col-span-1'>
              <p className='text-xs sm:text-sm font-medium text-gray-600'>
                Total Lectures
              </p>
              <p className='text-sm sm:text-lg text-gray-900'>
                {lectures.length}
              </p>
            </div>
          </div>
          <div className='mt-4'>
            <p className='text-xs sm:text-sm font-medium text-gray-600'>
              Description
            </p>
            <p className='text-sm sm:text-base text-gray-900 mt-1'>
              {batchData.description}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className='bg-white rounded-xl shadow-sm border'>
          <div className='border-b border-gray-200'>
            <nav className='flex px-4 sm:px-6'>
              <button
                onClick={() => setActiveTab('students')}
                className={`py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 mr-6 sm:mr-8 ${
                  activeTab === 'students'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Students ({students.length})
              </button>
              <button
                onClick={() => setActiveTab('lectures')}
                className={`py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 ${
                  activeTab === 'lectures'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Lectures ({lectures.length})
              </button>
            </nav>
          </div>

          <div className='p-4 sm:p-6'>
            {activeTab === 'students' && (
              <div>
                {/* Search Bar */}
                <div className='mb-4 sm:mb-6'>
                  <div className='relative'>
                    <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                    <input
                      type='text'
                      placeholder='Search students...'
                      value={searchStudent}
                      onChange={(e) => setSearchStudent(e.target.value)}
                      className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm sm:text-base'
                    />
                  </div>
                </div>

                {/* Students List */}
                <div className='space-y-3 sm:space-y-4'>
                  {filteredStudents.map((student) => (
                    <div
                      key={student.id}
                      className='flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors space-y-3 sm:space-y-0'
                    >
                      <div className='flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1'>
                        <div className='w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0'>
                          <span className='text-xs sm:text-sm font-medium text-blue-600'>
                            {(student.name
                              ? student.name
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')
                              : 'N/A'
                            ).toUpperCase()}
                          </span>
                        </div>
                        <div className='min-w-0 flex-1'>
                          <h4 className='text-sm sm:text-base font-medium text-gray-900'>
                            {student.name}
                            {student.admission_id && (
                              <span className='text-xs text-gray-500 ml-2'>
                                (ID: {student.admission_id})
                              </span>
                            )}
                          </h4>
                          <p className='text-xs sm:text-sm text-gray-500 truncate'>
                            {student.email}
                          </p>
                          {student.course && (
                            <p className='text-xs text-gray-500'>
                              Course: {student.course}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className='flex items-center justify-between sm:justify-end sm:space-x-4'>
                        <div className='flex items-center space-x-2 sm:space-x-4'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              student.status === 'Active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {student.status}
                          </span>
                          <p className='text-xs sm:text-sm text-gray-500 hidden sm:block'>
                            Enrolled:{' '}
                            {student.enrollDate
                              ? new Date(
                                  student.enrollDate,
                                ).toLocaleDateString()
                              : 'N/A'}
                          </p>
                        </div>

                        <button
                          onClick={() => handleDeleteBatchStd(student.id)}
                          className='p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0'
                          title='Delete Batch student'
                        >
                          <Trash2 className='w-4 h-4' />
                        </button>
                      </div>

                      <p className='text-xs text-gray-500 sm:hidden'>
                        Enrolled:{' '}
                        {student.enrollDate
                          ? new Date(student.enrollDate).toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'lectures' && (
              <div className='space-y-3 sm:space-y-4'>
                {isLoading ? (
                  <div className='text-center py-8'>
                    <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
                    <p className='mt-2 text-gray-600'>Loading lectures...</p>
                  </div>
                ) : lectures.length === 0 ? (
                  <div className='text-center py-8'>
                    <BookOpen className='mx-auto h-12 w-12 text-gray-400' />
                    <p className='mt-2 text-gray-600'>No lectures found</p>
                  </div>
                ) : (
                  lectures.map((lecture) => (
                    <div
                      key={lecture.id}
                      className='flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors space-y-3 sm:space-y-0'
                    >
                      <div className='flex items-start space-x-3 sm:space-x-4 min-w-0 flex-1'>
                        <div className='p-2 bg-purple-100 rounded-lg flex-shrink-0'>
                          <BookOpen className='w-4 h-4 sm:w-5 sm:h-5 text-purple-600' />
                        </div>
                        <div className='min-w-0 flex-1'>
                          <h4 className='text-sm sm:text-base font-medium text-gray-900 mb-1'>
                            {lecture.topic}
                          </h4>
                          <div className='space-y-1 text-xs sm:text-sm text-gray-500'>
                            <p>Teacher: {lecture.teacher}</p>
                            <p>Class Room: {lecture.seminar_hall}</p>
                            <p className='flex items-center'>
                              <Clock className='w-3 h-3 sm:w-4 sm:h-4 mr-1' />
                              {new Date(
                                lecture.lecture_start_time,
                              ).toLocaleString()}
                              -{' '}
                              {new Date(
                                lecture.lecture_end_time,
                              ).toLocaleTimeString()}
                            </p>
                            {lecture.note && (
                              <p className='text-gray-500'>
                                Note: {lecture.note}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-end sm:ml-4'>
                        <button
                          onClick={() => handleDeleteLecture(lecture.id)}
                          className='p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                          title='Delete lecture'
                        >
                          <Trash2 className='w-4 h-4' />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add Student Modal */}
      {showAddStudentModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto'>
            <div className='flex items-center justify-between p-4 sm:p-6 border-b'>
              <h2 className='text-lg sm:text-xl font-semibold text-gray-900'>
                Add Students to Batch
              </h2>
              <button
                onClick={handleCloseAddStudentModal}
                className='text-gray-400 hover:text-gray-600 transition-colors'
              >
                <X className='w-6 h-6' />
              </button>
            </div>

            <div className='p-4 sm:p-6 space-y-4'>
              {/* Search & Status Filter Grid */}
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='student-search'
                    className='block text-sm font-medium text-gray-700 mb-1'
                  >
                    Search Students
                  </label>
                  <div className='relative'>
                    <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                    <input
                      type='text'
                      id='student-search'
                      value={searchStudent}
                      onChange={(e) => setSearchStudent(e.target.value)}
                      className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm'
                      placeholder='Search name, email...'
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor='status-filter'
                    className='block text-sm font-medium text-gray-700 mb-1'
                  >
                    Status Filter
                  </label>
                  <select
                    id='status-filter'
                    value={modalStatusFilter}
                    onChange={(e) => setModalStatusFilter(e.target.value)}
                    className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white'
                  >
                    <option value='Running'>Running</option>
                    <option value='Left'>Left</option>
                    <option value='Completed'>Completed</option>
                  </select>
                </div>
              </div>

              {/* Selected Students Count */}
              {selectedStudents.length > 0 && (
                <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
                  <p className='text-sm text-blue-800'>
                    {selectedStudents.length} student(s) selected to add
                  </p>
                </div>
              )}

              {/* Student Selection List with Checkboxes */}
              <div className='max-h-48 sm:max-h-60 overflow-y-auto border border-gray-300 rounded-lg'>
                {filteredModalStudents.length === 0 ? (
                  <p className='p-3 text-sm text-gray-500 text-center'>
                    No students found with status "{modalStatusFilter}".
                  </p>
                ) : (
                  filteredModalStudents.map((student) => {
                    const isAdded = isAlreadyAdded(student)
                    return (
                      <label
                        key={student.id}
                        className={`flex items-center p-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                          isAdded
                            ? 'bg-gray-50 cursor-not-allowed opacity-75'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type='checkbox'
                          checked={
                            isAdded ||
                            selectedStudents.includes(student.id.toString())
                          }
                          disabled={isAdded}
                          onChange={(e) => {
                            if (isAdded) return
                            const studentId = student.id.toString()
                            if (e.target.checked) {
                              setSelectedStudents((prev) => [
                                ...prev,
                                studentId,
                              ])
                            } else {
                              setSelectedStudents((prev) =>
                                prev.filter((id) => id !== studentId),
                              )
                            }
                          }}
                          className='form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded disabled:bg-gray-300 disabled:text-gray-400 disabled:border-gray-300'
                        />
                        <div className='ml-3 min-w-0 flex-1'>
                          <span className='text-sm font-medium text-gray-700 block'>
                            {student.name}{' '}
                            {isAdded && (
                              <span className='ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800'>
                                Already Added
                              </span>
                            )}
                          </span>
                          {student.email && (
                            <span className='text-xs text-gray-500 block'>
                              {student.email}
                            </span>
                          )}
                          {student.course && (
                            <span className='text-xs text-gray-500 block'>
                              Course: {student.course}
                            </span>
                          )}
                        </div>
                      </label>
                    )
                  })
                )}
              </div>

              {/* Select/Deselect All Buttons */}
              <div className='flex space-x-2'>
                <button
                  type='button'
                  onClick={() => {
                    const actionableStudents = filteredModalStudents.filter(
                      (student) => !isAlreadyAdded(student),
                    )
                    setSelectedStudents(
                      actionableStudents.map((student) =>
                        student.id.toString(),
                      ),
                    )
                  }}
                  className='flex-1 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors'
                >
                  Select All
                </button>
                <button
                  type='button'
                  onClick={() => setSelectedStudents([])}
                  className='flex-1 px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors'
                >
                  Deselect All
                </button>
              </div>

              {/* Displaying Batch ID for confirmation */}
              <div className='flex items-center justify-between p-3 border border-gray-300 rounded-lg text-sm text-gray-700'>
                <span>Batch ID:</span>
                <span className='font-semibold'>{batchData.code || id}</span>
              </div>

              <div className='flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4'>
                <button
                  type='button'
                  onClick={handleCloseAddStudentModal}
                  className='w-full sm:flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors'
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMultipleStudents}
                  disabled={selectedStudents.length === 0 || isLoading}
                  className='w-full sm:flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:bg-green-400'
                >
                  {isLoading
                    ? 'Adding...'
                    : `Add ${selectedStudents.length} Student(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Lecture Modal */}
      {showCreateLectureModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto'>
            <div className='flex items-center justify-between p-4 sm:p-6 border-b'>
              <h2 className='text-lg sm:text-xl font-semibold text-gray-900'>
                Create New Lecture
              </h2>
              <button
                onClick={() => setShowCreateLectureModal(false)}
                className='text-gray-400 hover:text-gray-600 transition-colors'
              >
                <X className='w-6 h-6' />
              </button>
            </div>

            <div className='p-4 sm:p-6 space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Teacher *
                </label>
                <input
                  type='text'
                  value={newLecture.teacher}
                  onChange={(e) =>
                    setNewLecture({ ...newLecture, teacher: e.target.value })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm'
                  placeholder='Enter teacher name'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Topic *
                </label>
                <input
                  type='text'
                  value={newLecture.topic}
                  onChange={(e) =>
                    setNewLecture({ ...newLecture, topic: e.target.value })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm'
                  placeholder='Enter lecture topic'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Lecture Date *
                </label>
                <input
                  type='date'
                  value={newLecture.lecture_date}
                  onChange={(e) =>
                    setNewLecture({
                      ...newLecture,
                      lecture_date: e.target.value,
                    })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm'
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Start Time *
                  </label>
                  <input
                    type='time'
                    value={newLecture.lecture_start_time}
                    onChange={(e) =>
                      setNewLecture({
                        ...newLecture,
                        lecture_start_time: e.target.value,
                      })
                    }
                    className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    End Time *
                  </label>
                  <input
                    type='time'
                    value={newLecture.lecture_end_time}
                    onChange={(e) =>
                      setNewLecture({
                        ...newLecture,
                        lecture_end_time: e.target.value,
                      })
                    }
                    className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm'
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Seminar Hall *
                </label>
                <input
                  type='text'
                  value={newLecture.seminar_hall}
                  onChange={(e) =>
                    setNewLecture({
                      ...newLecture,
                      seminar_hall: e.target.value,
                    })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm'
                  placeholder='Enter seminar hall'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Note
                </label>
                <textarea
                  value={newLecture.note}
                  onChange={(e) =>
                    setNewLecture({
                      ...newLecture,
                      note: e.target.value,
                    })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm'
                  rows='3'
                  placeholder='Enter additional notes'
                />
              </div>

              <div className='flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4'>
                <button
                  type='button'
                  onClick={() => setShowCreateLectureModal(false)}
                  className='w-full sm:flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors'
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateLecture}
                  disabled={isLoading}
                  className='w-full sm:flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors'
                >
                  {isLoading ? 'Creating...' : 'Create Lecture'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BatchDetails
